<template>
  <div class="admin-organization-registers-list">
    <el-select
      v-model="recentOrgListFilter"
      placeholder="Filters"
      v-if="!organizationId"
    >
      <el-option
        v-for="item in recentOrgListFilterOpts"
        :key="item.value"
        :label="item.label"
        :value="item.value"
      />
    </el-select>

    <el-table
      v-loading="actionInProgress"
      :data="registrations"
      v-if="registrations && registrations.length"
    >
      <el-table-column label="Date">
        <template slot-scope="{ row }">
          {{ row.ts | date }}
        </template>
      </el-table-column>
      <el-table-column label="User">
        <template slot-scope="{ row }">
          <CopyClick :text="row.user.email">
            {{ row.user.email }}
          </CopyClick>
        </template>
      </el-table-column>
      <el-table-column label="Organization">
        <template slot-scope="{ row }">
          <router-link
            :to="getRouteToOrganizationEdit(row.organization.id)"
            custom
            v-slot="{ href }"
          >
            <a
              :href="href"
              @click="
                (event) =>
                  navigateToRouteToOrganizationEdit(row.organization.id, event)
              "
            >
              {{ row.organization.name }}
              <ion-icon name="link-outline" class="pt2" />
            </a>
          </router-link>
        </template>
      </el-table-column>
      <el-table-column label="Status">
        <template slot-scope="{ row }">
          <span class="label label-default" v-if="getRegOrgStatus(row)">{{
            getRegOrgStatus(row)
          }}</span>

          <button
            class="btn btn-transparent btn-xs btn-success"
            @click="
              startApprove({
                userId: row.user.id,
                organizationId: row.organization.id,
                organizationName: row.organization.name,
              })
            "
            v-if="showApprove(row)"
          >
            Approve
          </button>
          <button
            class="btn btn-transparent btn-xs btn-warning mr-2"
            @click="
              startReject({
                userId: row.user.id,
                organizationId: row.organization.id,
                organizationName: row.organization.name,
              })
            "
            v-if="showReject(row)"
          >
            Reject
          </button>
          <TextButton
            type="danger"
            size="sm"
            v-if="showHide(row)"
            @click="
              startHide({
                userId: row.user.id,
                organizationId: row.organization.id,
                organizationName: row.organization.name,
              })
            "
          >
            Hide
          </TextButton>
        </template>
      </el-table-column>
      <el-table-column label="Type">
        <template slot-scope="{ row }">
          {{ row.organization.type || 'VC' }}
        </template>
      </el-table-column>
    </el-table>

    <el-pagination
      class="mt-12"
      background
      layout="prev, pager, next, total"
      :page-size="pageSize"
      :current-page.sync="page"
      :total="registrationsCount"
      @current-change="onPageChange"
      v-if="!organizationId"
    />

    <el-dialog
      :visible.sync="isModalVisible"
      :title="
        shouldBeApproved ? 'Approval confirmation' : 'Reject confirmation'
      "
      :close-on-click-modal="false"
    >
      <div v-loading="approveInProgress">
        <el-form>
          <el-form-item label="Type">
            <el-radio v-model="approvalModalData.type" label="VC">
              VC
            </el-radio>
            <el-radio v-model="approvalModalData.type" label="CORP">
              Corp
            </el-radio>
          </el-form-item>
        </el-form>

        <el-button
          v-if="shouldBeApproved"
          type="primary"
          size="small"
          @click="
            onApproveRegistrationConfirmed({
              userId: approvalModalData.userId,
              organizationId: approvalModalData.organizationId,
            })
          "
        >
          Approve
        </el-button>
        <el-button
          v-else
          type="primary"
          size="small"
          @click="
            onRejectRegistrationConfirmed({
              userId: approvalModalData.userId,
              organizationId: approvalModalData.organizationId,
            })
          "
        >
          Reject
        </el-button>
        <el-button size="small" @click="isModalVisible = false">
          Cancel
        </el-button>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import { ref, watch, computed, onMounted } from '@vue/composition-api'
import http from 'services/http'
import CopyClick from '@/components/CopyClick'
import TextButton from 'components/common/TextButton'
import useOrganizationRegistrationList from '@/states/admin-organization-registrations'
import { update as updateOrganization } from '@/services/organizations'
import router from '@/routes'

export default {
  components: {
    TextButton,
    CopyClick,
  },
  props: {
    organizationId: {
      type: String,
      default: null,
    },
    enabled: {
      type: Boolean,
      default: true,
    },
  },
  setup(props, { root }) {
    const {
      registrations,
      registrationsCount,
      fetchRegistrations,
      approveRegistrationInProgress,
      approveRegistration,
      rejectRegistrationInProgress,
      rejectRegistration,
      hideRegistrationInProgress,
      hideRegistration,
      setOrganizationId,
    } = useOrganizationRegistrationList({
      organizationId: props.organizationId,
    })

    const getRouteToOrganizationEdit = (organizationId) => {
      const route = router.resolve({
        name: 'adminOrganizationEdit',
        params: { organizationId },
      })
      return route.href
    }
    const navigateToRouteToOrganizationEdit = (organizationId, event) => {
      event.stopPropagation()
      event.preventDefault()
      location.href = getRouteToOrganizationEdit(organizationId)
    }

    const page = ref(1)
    const pageSize = ref(24)

    const recentOrgListFilterOpts = ref([
      {
        value: 'all',
        label: 'Show all',
      },
      {
        value: 'hidden',
        label: 'Show hidden only',
      },
      {
        value: 'in_review',
        label: 'Show in review only',
      },
      {
        value: 'approved',
        label: 'Show approved only',
      },
      {
        value: 'auto_approved',
        label: 'Show auto-approved only (ACC)',
      },
      {
        value: 'rejected',
        label: 'Show rejected only',
      },
    ])
    const recentOrgListFilter = ref('all')

    const actionInProgress = computed(() => {
      return (
        approveRegistrationInProgress.value ||
        rejectRegistrationInProgress.value ||
        hideRegistrationInProgress.value
      )
    })

    const fetch = (
      params = { page: page.value, status: recentOrgListFilter.value },
    ) => {
      setOrganizationId(props.organizationId)
      fetchRegistrations(params)
    }

    onMounted(() => {
      if (props.enabled) {
        fetch()
      }
    })

    watch(recentOrgListFilter, (val) => {
      fetch({ page: 1, status: val })
    })
    watch(
      () => props.enabled,
      () => {
        if (props.enabled) {
          fetch()
        }
      },
    )

    const approveInProgress = ref(false)
    const approvalModalData = ref({})

    const onApproveRegistrationConfirmed = async ({
      userId,
      organizationId,
    }) => {
      if (approveRegistrationInProgress.value) {
        root.$message.error('Another approval in progress')
        return
      }
      approveInProgress.value = true
      const { organizationName: name, type } = approvalModalData.value
      await updateOrganization(organizationId, { name, type })
      await approveRegistration({ userId, organizationId })
      root.$message.success('Registration approved!')
      approveInProgress.value = true
      isModalVisible.value = false
    }

    const onRejectRegistrationConfirmed = async ({
      userId,
      organizationId,
    }) => {
      if (rejectRegistrationInProgress.value) {
        root.$message.error('Another reject in progress')
        return
      }

      approveInProgress.value = true
      const { organizationName: name, type } = approvalModalData.value
      await updateOrganization(organizationId, { name, type })
      await rejectRegistration({ userId, organizationId })
      root.$message.success('Registration rejected!')
      approveInProgress.value = true
      isModalVisible.value = false
    }

    const IN_REVIEW = 'in_review'
    const APPROVED = 'approved'
    const REJECTED = 'rejected'
    const HIDDEN = 'hidden'

    const isModalVisible = ref(false)
    const shouldBeApproved = ref(false)
    const startApprove = ({ userId, organizationId, organizationName }) => {
      showApproveModal({
        userId,
        organizationId,
        organizationName,
        approve: true,
      })
    }
    const startReject = ({ userId, organizationId, organizationName }) => {
      showApproveModal({
        userId,
        organizationId,
        organizationName,
        approve: false,
      })
    }
    const showApproveModal = ({
      userId,
      organizationId,
      organizationName,
      approve,
    }) => {
      isModalVisible.value = true
      shouldBeApproved.value = approve
      approvalModalData.value = {
        organizationId,
        organizationName,
        userId,
        approve,
        type: 'VC',
      }
    }

    const startHide = ({ userId, organizationId, organizationName }) => {
      root
        .$confirm(
          `Are you sure you want to hide registration of: ${organizationName}`,
          {
            confirmButtonText: 'Confirm',
            cancelButtonText: 'Cancel',
          },
        )
        .then(async () => {
          await hideRegistration({ userId, organizationId })
          await fetch()
        })
        .catch(() => {})
    }

    const onPageChange = () => {
      fetch()
    }

    const getRegOrgStatus = ({ status }) => {
      switch (status) {
        case 'approved':
          return 'Approved'
        case 'auto_approved':
          return 'Auto-Approved'
        case 'rejected':
          return 'Rejected'
        case 'hidden':
          return 'Hidden'
        default:
          return ''
      }
    }

    const showApprove = ({ status }) => {
      if (status === 'in_review' && props.organizationId) {
        return true
      }
      return (
        !props.organizationId &&
        ['in_review', 'hidden', 'rejected'].includes(status)
      )
    }

    const showReject = ({ status }) => {
      if (status === 'in_review' && props.organizationId) {
        return true
      }
      return !props.organizationId && ['in_review', 'hidden'].includes(status)
    }

    const showHide = ({ status }) => {
      return ['in_review'].includes(status)
    }

    return {
      actionInProgress,

      approveInProgress,
      registrations,
      registrationsCount,
      onApproveRegistrationConfirmed,
      approveRegistrationInProgress,
      onRejectRegistrationConfirmed,
      rejectRegistrationInProgress,

      isModalVisible,
      approvalModalData,
      shouldBeApproved,
      startApprove,
      startReject,
      startHide,

      IN_REVIEW,
      APPROVED,
      REJECTED,
      HIDDEN,

      onPageChange,
      page,
      pageSize,

      recentOrgListFilterOpts,
      recentOrgListFilter,

      getRegOrgStatus,
      showApprove,
      showReject,
      showHide,

      getRouteToOrganizationEdit,
      navigateToRouteToOrganizationEdit,
    }
  },
}
</script>
